import React from "react";
import { Button, Modal, Form, Table } from "react-bootstrap";
import { useMobileLicensing } from "./useMobileLicensing";
import DatePicker from "react-multi-date-picker";
import InputIcon from "react-multi-date-picker/components/input_icon";
import { ILicensingDetail } from "../../../common/types/mobileLicensingTypes";
import { ACTION } from "../../../common/constants";

interface IProps {
  orgId:string;
  orgName: string;
  onClose: () => void;
  show: boolean;
  license:ILicensingDetail;
  action: (typeof ACTION)[keyof typeof ACTION];
}
const CustomDatePicker = ({date,handleDateChange, minDate}) => {
  return (
    <div className="mobile-licensing-date-picker">
      <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
        <Form.Label>Expire Date</Form.Label>
        <DatePicker
          inputClass="licensing-custom-input"
          render={<InputIcon placeholder="MM/DD/YYYY"/>}
          value={date}
          format="MM/DD/YYYY"
          onChange={handleDateChange}
          minDate={minDate}
        />
      </Form.Group>
    </div>
  );
};
const MobileLicensingModal = (props:IProps) => {
  const { license, action, show, onClose, orgName,orgId} = props;
  const {
    licenseCount,
    setLicenseCount,
    usedLicenseCount,
    expireDate,
    handleExpireDateChange,
    handleMobileLicenseAction,
    handleClose,
    isManageConfirmationVisible,
    handleManageBack,
    shouldShowManageWarning,
    licensesToBeRevoked,
  } = useMobileLicensing(license,orgId,onClose);
  
  const todayForMinDate = new Date();
  todayForMinDate.setHours(0, 0, 0, 0);

  const managePrimaryButtonLabel = isManageConfirmationVisible
    ? "Confirm"
    : "Manage";

  const manageModalTitle = isManageConfirmationVisible
    ? `Confirm ${license.name}`
    : `Manage ${license.name}`;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        className="modal-container create-new-org-admin"
        centered
      >
        <Form>
          <Modal.Header closeButton>
            <Modal.Title>
              {action === ACTION.ENABLE && `Enable ${license.name}`}
              {action === ACTION.DISABLE && `Disable ${license.name}`}
              {action === ACTION.MANAGE && manageModalTitle}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {action === ACTION.ENABLE && (
              <>
                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label>Organization Name</Form.Label>
                  <Form.Control type="text" value={orgName} disabled />
                </Form.Group>
                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label>Total Number of Licenses</Form.Label>
                  <Form.Control
                    type="number"
                    value={licenseCount}
                    min={"1"}
                    placeholder="Enter number of licenses (min 1)"
                    onChange={(e) => setLicenseCount(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid number of licenses.
                  </Form.Control.Feedback>
                </Form.Group>
               <CustomDatePicker date={expireDate} handleDateChange={handleExpireDateChange} minDate={todayForMinDate} />
              </>
            )}

            {action === ACTION.DISABLE && (
              <>
                <p>
                  Are you sure you want to disable{" "}
                  <strong>Mobile License</strong> for <strong>{orgName}</strong>{" "}
                  Organization?
                </p>

                <table className="table table-bordered mb-3">
                  <thead>
                    <tr>
                      <th colSpan={2} className="bg-light text-center">
                        Mobile Licensing Summary
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Total Licenses</strong>
                      </td>
                      <td>{licenseCount}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Licenses In Use</strong>
                      </td>
                      <td>{usedLicenseCount}</td>
                    </tr>
                  </tbody>
                </table>

                <p className="text-danger">
                  Users currently using Mobile Licensing will lose access
                  immediately after disabling.
                </p>
              </>
            )}

            {action === ACTION.MANAGE && !isManageConfirmationVisible && (
              <>
                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label>Organization Name</Form.Label>
                  <Form.Control type="text" value={orgName} disabled />
                </Form.Group>

                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label>Total Number of Licenses</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter number of licenses (min 1)"
                    value={licenseCount}
                    min={"1"}
                    onChange={(event) => setLicenseCount(event.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid number of licenses.
                  </Form.Control.Feedback>
                </Form.Group>

                <CustomDatePicker
                  date={expireDate}
                  handleDateChange={handleExpireDateChange}
                  minDate={todayForMinDate}
                />
              </>
            )}
            {action === ACTION.MANAGE && isManageConfirmationVisible && (
              <>
                <p>
                  Are you sure you want to update{" "}
                  <strong>Mobile License</strong> for <strong>{orgName}</strong>{" "}
                  Organization?
                </p>

                <table className="table table-bordered mb-3">
                  <thead>
                    <tr>
                      <th colSpan={2} className="bg-light text-center">
                        Mobile Licensing Summary
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Total Licenses</strong>
                      </td>
                      <td>{licenseCount}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Licenses In Use</strong>
                      </td>
                      <td>{usedLicenseCount}</td>
                    </tr>
                  </tbody>
                </table>
                
                {shouldShowManageWarning && (
                  <p className="text-danger mb-0">
                    {licensesToBeRevoked} currently assigned mobile{" "}
                    {licensesToBeRevoked === 1 ? "license will" : "licenses will"}{" "}
                    be revoked immediately after this change.
                  </p>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {action === ACTION.MANAGE && isManageConfirmationVisible && (
              <Button className="btn-cancel" onClick={handleManageBack}>
                Back
              </Button>
            )}
            <Button className="btn-style" onClick={handleMobileLicenseAction(action)} variant="primary" type="button" disabled={
                action === ACTION.ENABLE &&
                (Number(licenseCount) < 1 || !expireDate)
              }  >
               {action === ACTION.MANAGE ? managePrimaryButtonLabel : action}
            </Button>{" "}
            <Button className="btn-cancel" onClick={handleClose}>
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default MobileLicensingModal;
